import React from 'react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import {PhoneDetails} from '@chekin/core';
import PhoneCodesSelect from './PhoneCodesSelect';
import {ErrorMessage, Input} from '@guestapp/old-ui-kit';
import {Wrapper} from './styled';

type CountryCodeOption = {
  name: string;
  code: string;
};

export type PhoneInputHandle = {
  getCodeSearchValue: () => string;
  setCodeSearchValue: (value: string) => void;
  getNumberValue: () => string;
  setNumberValue: (value: string) => void;
};

const isPhoneNumberDetails = (
  value?: string | null | number | PhoneDetails,
): value is PhoneDetails => {
  if (typeof value === 'string' || typeof value === 'number') {
    return false;
  }

  return !!value?.code;
};

export type PhoneInputProps = {
  onChange: (value: string, name?: string, details?: PhoneDetails) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onBlur?: () => void;
  name?: string;
  label?: string;
  disabled?: boolean;
  defaultInputValue?: string;
  defaultCode?: string;
  placeholder?: string;
  value?: string | number | null | PhoneDetails; // NOTE: Use null to reset fields
  error?: string;
  tooltipContent?: string;
  codeReadOnly?: boolean;
  className?: string;
  virtualKeyboardRef?: React.RefObject<PhoneInputHandle>;
  onFieldFocus?: (fieldType: 'code' | 'number') => void;
  onFieldBlur?: (fieldType: 'code' | 'number') => void;
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  (
    {
      onChange = () => {},
      onBlur,
      name,
      label,
      disabled,
      defaultCode,
      defaultInputValue,
      placeholder,
      value,
      error,
      codeReadOnly,
      tooltipContent,
      onKeyDown,
      virtualKeyboardRef,
      onFieldFocus,
      onFieldBlur,
    },
    ref,
  ) => {
    const [selectedPhoneCodeOption, setSelectedPhoneCodeOption] =
      React.useState<CountryCodeOption | null>(null);
    const preloadedInputValue =
      defaultInputValue || (isPhoneNumberDetails(value) && value?.number);
    const preloadedCodeValue =
      defaultCode || (isPhoneNumberDetails(value) && value?.code);
    const [inputValue, setInputValue] = React.useState(preloadedCodeValue || '');
    const [searchValue, setSearchValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);

    const {t} = useTranslation();

    React.useEffect(() => {
      setInputValue(preloadedInputValue || '');
    }, [preloadedInputValue]);

    React.useEffect(() => {
      if (preloadedCodeValue) {
        const option = {name: '', code: preloadedCodeValue};
        option && setSelectedPhoneCodeOption(option);
      }
    }, [preloadedCodeValue, setSelectedPhoneCodeOption]);

    React.useEffect(() => {
      if (value === null) {
        setInputValue('');
        setSelectedPhoneCodeOption(null);
      }
    }, [value]);

    React.useImperativeHandle(
      virtualKeyboardRef,
      () => ({
        getCodeSearchValue: () => searchValue,
        setCodeSearchValue: (value: string) => setSearchValue(value),
        getNumberValue: () => inputValue,
        setNumberValue: (value: string) => {
          setInputValue(value);

          if (!value || !selectedPhoneCodeOption?.code) {
            onChange('', name, undefined);
            return;
          }

          const nextValue = `${selectedPhoneCodeOption.code}${value}`;
          onChange(nextValue, name, {
            code: selectedPhoneCodeOption.code,
            number: value,
          });
        },
      }),
      [searchValue, inputValue, selectedPhoneCodeOption, name, onChange],
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const {target} = event;
      setInputValue(target.value);

      if (!target.value || !selectedPhoneCodeOption?.code) {
        onChange('', name, undefined);
        return;
      }

      const nextValue = `${selectedPhoneCodeOption.code}${target.value}`;
      onChange(nextValue, name, {
        code: selectedPhoneCodeOption.code,
        number: target.value,
      });
    };

    const handleCountryCodeChange = React.useCallback(
      (option: CountryCodeOption) => {
        setSelectedPhoneCodeOption(option);

        if (!inputValue) {
          onChange('', name, undefined);
          return;
        }

        const nextValue = `${option.code}${inputValue}`;
        onChange(nextValue, name, {
          code: option.code,
          number: inputValue,
        });
      },
      [inputValue, name, onChange],
    );

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (disabled) return;
      onKeyDown?.(event);
    };

    const handleNumberFocus = () => {
      setIsFocused(true);
      onFieldFocus?.('number');
    };

    const handleNumberBlur = () => {
      onBlur?.();
      setIsFocused(false);
      onFieldBlur?.('number');
    };

    return (
      <>
        <Wrapper
          $error={error}
          className={clsx('phone-field', {
            'phone-field--disabled': disabled,
            'phone-field--invalid': error,
            'phone-field--empty': !inputValue,
          })}
        >
          <PhoneCodesSelect
            value={selectedPhoneCodeOption}
            defaultCode={defaultCode}
            disabled={disabled}
            readOnly={codeReadOnly}
            error={error}
            onChange={handleCountryCodeChange}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onFocus={() => onFieldFocus?.('code')}
            onBlur={() => onFieldBlur?.('code')}
          />
          <Input
            ref={ref}
            onChange={handleInputChange}
            onBlur={handleNumberBlur}
            onFocus={handleNumberFocus}
            type="number"
            label={label}
            disabled={disabled}
            value={inputValue}
            empty={!inputValue}
            tooltipContent={tooltipContent}
            placeholder={placeholder}
            inputMode="tel"
            aria-label={name}
            onKeyDown={handleKeyDown}
            className={clsx('phone-field__input', 'kiosko:[&_input]:pl-[90px]', {
              'kiosko:[&_label]:!left-[90px]': !isFocused,
            })}
          />
        </Wrapper>
        <div>
          {!error && !selectedPhoneCodeOption && inputValue && (
            <ErrorMessage>{t('prefix_required')}</ErrorMessage>
          )}
          {error && <ErrorMessage>{error as string}</ErrorMessage>}
        </div>
      </>
    );
  },
);

export {PhoneInput};
